import { Injectable } from '@angular/core';
import { ApiService } from './jrni/api.service';
import { DatastoreService } from './datastore.service';
import { citizenshipAttendeesAnswer, citizenshipCapacityOutcome, citizenshipPartySize } from './citizenship-capacity';

/**
 * Works out which group citizenship days and sessions are full, so the calendar can hide them.
 *
 * JRNI shows a group session as open until the whole ceremony is full, so it cannot say how many
 * places are left. The citizenship availability endpoint can, one session per call. This service
 * asks it about the sessions the calendar gives it and remembers the answers.
 */
@Injectable({
  providedIn: 'root',
})
export class CitizenshipPlacesService {
  private places = new Map();
  private requests = new Map();
  private fullDays = new Set();
  private checkedMonths = new Map();
  // Goes up on every reset, so a check that started before it knows its answer is out of date.
  private resetCount = 0;

  constructor(
    private apiService: ApiService,
    private datastoreService: DatastoreService
  ) {}

  /** Forgets every answer, for example after a venue change. */
  reset() {
    this.places = new Map();
    this.requests = new Map();
    this.fullDays = new Set();
    this.checkedMonths = new Map();
    this.resetCount++;
  }

  hasChecked(month) {
    return this.checkedMonths.has(month);
  }

  isDayFull(date) {
    return this.fullDays.has(date);
  }

  /** True when we know the session has too few places. A session we could not read counts as not full. */
  isSessionFull(start) {
    // The same party size the click check on the calendar uses.
    const placesNeeded = citizenshipPartySize(citizenshipAttendeesAnswer(this.datastoreService.selectedSchema)).places;
    return citizenshipCapacityOutcome(this.places.get(sessionKey(start)), placesNeeded) === 'full';
  }

  /** Saves an answer the calendar got itself, for example when a time is clicked. */
  remember(start, availability) {
    this.places.set(sessionKey(start), availability);
  }

  /** Reads the places left on these sessions, six at a time, the most a browser sends to one host at once. */
  async loadSessions(starts, location, service) {
    const resetCount = this.resetCount;
    // After a reset these still point at the old maps, so a late answer cannot mix two venues.
    const places = this.places;
    const requests = this.requests;

    for (let i = 0; i < starts.length; i += 6) {
      const batch = starts.slice(i, i + 6);
      await Promise.all(
        batch.map((start) => {
          const key = sessionKey(start);
          if (requests.has(key) || resetCount !== this.resetCount) {
            return requests.get(key);
          }
          const request = this.apiService
            .citizenshipAvailability(location.id, service.id, start.split('+')[0])
            .then((availability) => places.set(key, availability));
          requests.set(key, request);
          return request;
        })
      );
    }
  }

  /**
   * Reads every session in a month and marks the days where none has room.
   * sessionsByDay maps each day (YYYY-MM-DD) to its session start times. Each month is only checked once.
   */
  checkMonth(month, sessionsByDay, location, service) {
    if (!this.checkedMonths.has(month)) {
      this.checkedMonths.set(month, this.markFullDays(sessionsByDay, location, service));
    }
    return this.checkedMonths.get(month);
  }

  private async markFullDays(sessionsByDay, location, service) {
    const resetCount = this.resetCount;
    const fullDays = this.fullDays;
    const sessions: Map<string, string[]> = await sessionsByDay;

    const allStarts = [];
    sessions.forEach((starts) => allStarts.push(...starts));
    await this.loadSessions(allStarts, location, service);
    if (resetCount !== this.resetCount) {
      return;
    }

    // A day with no bookable times counts as full too.
    sessions.forEach((starts, day) => {
      if (starts.every((start) => this.isSessionFull(start))) {
        fullDays.add(day);
      }
    });
  }
}

/**
 * The key a session's places are saved under: "2026-10-06T10:00:00+01:00" becomes "2026-10-06T10:00".
 * The availability endpoint matches sessions to the minute and ignores the time zone offset.
 */
function sessionKey(start) {
  return String(start).split('+')[0].slice(0, 16);
}
